import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BackofficeLayout from '@/components/backoffice/BackofficeLayout';
import {
  financeiroEmpresas,
  getRecompensaTipoLabel,
  getRecompensaTipoColor,
} from '@/mocks/backoffice/financeiroConsolidado';
import RecompensasChart from './components/RecompensasChart';
import YoYBadget from './components/YoYBadge';

export default function BackofficeFinanceiroDashboardPage() {
  const navigate = useNavigate();
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [filtroPlano, setFiltroPlano] = useState<string>('todos');
  const [periodo, setPeriodo] = useState<string>('6m');
  const [abaAtiva, setAbaAtiva] = useState<'geral' | 'faturamento' | 'cadastros' | 'recompensas'>('geral');

  // Período filter logic
  const periodoConfig = useMemo(() => {
    switch (periodo) {
      case '3m': return { mesesIdx: [3, 4, 5], label: 'Últimos 3 meses' };
      case '6m': return { mesesIdx: [0, 1, 2, 3, 4, 5], label: 'Últimos 6 meses' };
      case 'ytd': return { mesesIdx: [1, 2, 3, 4, 5], label: 'Ano até hoje' };
      default: return { mesesIdx: [0, 1, 2, 3, 4, 5], label: 'Últimos 6 meses' };
    }
  }, [periodo]);

  const empresasFiltradas = useMemo(() => {
    return financeiroEmpresas.filter((e) => {
      if (filtroStatus !== 'todos' && e.status !== filtroStatus) return false;
      if (filtroPlano !== 'todos' && e.plano !== filtroPlano) return false;
      return true;
    });
  }, [filtroStatus, filtroPlano]);

  const empresasAtivas = empresasFiltradas.filter((e) => e.status === 'ativa');

  // KPIs consolidados
  const kpi = useMemo(() => {
    const faturamentoTotal = empresasAtivas.reduce((sum, e) => sum + e.faturamento_mensal, 0);
    const faturamentoAcumulado = empresasFiltradas.reduce((sum, e) => sum + e.faturamento_acumulado, 0);
    const participantesAtivos = empresasAtivas.reduce((sum, e) => sum + e.participantes_ativos, 0);
    const novosCadastros = empresasAtivas.reduce((sum, e) => sum + e.novos_cadastros_mes, 0);
    const totalRecompensasValor = empresasFiltradas.reduce((sum, e) => sum + e.total_recompensas_valor, 0);
    const totalRecompensasQtd = empresasFiltradas.reduce((sum, e) => sum + e.total_recompensas_quantidade, 0);
    return { faturamentoTotal, faturamentoAcumulado, participantesAtivos, novosCadastros, totalRecompensasValor, totalRecompensasQtd };
  }, [empresasFiltradas, empresasAtivas]);

  // YoY consolidado
  const yoy = useMemo(() => {
    const faturamentoTotal = empresasAtivas.reduce((sum, e) => sum + e.yoy.faturamento_mensal, 0);
    const faturamentoAcumulado = empresasFiltradas.reduce((sum, e) => sum + e.yoy.faturamento_acumulado, 0);
    const participantesAtivos = empresasAtivas.reduce((sum, e) => sum + e.yoy.participantes_ativos, 0);
    const novosCadastros = empresasAtivas.reduce((sum, e) => sum + e.yoy.novos_cadastros_mes, 0);
    const totalRecompensasValor = empresasFiltradas.reduce((sum, e) => sum + e.yoy.total_recompensas_valor, 0);
    const totalRecompensasQtd = empresasFiltradas.reduce((sum, e) => sum + e.yoy.total_recompensas_quantidade, 0);
    return { faturamentoTotal, faturamentoAcumulado, participantesAtivos, novosCadastros, totalRecompensasValor, totalRecompensasQtd };
  }, [empresasFiltradas, empresasAtivas]);

  // Históricos filtrados por período
  const mesesLabels = ['Dez', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai'];
  const mesesAtivos = periodoConfig.mesesIdx;

  const faturamentoHistorico = useMemo(() => {
    return mesesAtivos.map((idx) => {
      const valor = empresasFiltradas.reduce((sum, e) => sum + (e.faturamento_historico[idx]?.valor || 0), 0);
      return { mes: mesesLabels[idx], valor };
    });
  }, [empresasFiltradas, mesesAtivos]);

  const maxFaturamento = Math.max(...faturamentoHistorico.map((f) => f.valor), 1);

  const cadastrosHistorico = useMemo(() => {
    return mesesAtivos.map((idx) => {
      const novos = empresasFiltradas.reduce((sum, e) => sum + (e.cadastros_historico[idx]?.novos || 0), 0);
      const desligados = empresasFiltradas.reduce((sum, e) => sum + (e.cadastros_historico[idx]?.desligados || 0), 0);
      return { mes: mesesLabels[idx], novos, desligados };
    });
  }, [empresasFiltradas, mesesAtivos]);

  const maxCadastros = Math.max(...cadastrosHistorico.map((c) => c.novos + c.desligados), 1);

  // Recompensas histórico consolidado
  const recompensasHistorico = useMemo(() => {
    return mesesAtivos.map((idx) => {
      const result: Record<string, number> = { mes: mesesLabels[idx] };
      const tipos = ['pix_valor', 'pagamento_conta_valor', 'voucher_valor', 'curso_valor', 'experiencia_valor', 'produto_valor'];
      tipos.forEach((tipo) => {
        result[tipo] = empresasFiltradas.reduce((sum, e) => {
          return sum + (e.recompensas_historico[idx]?.[tipo as keyof typeof e.recompensas_historico[0]] as number || 0);
        }, 0);
      });
      return result as {
        mes: string;
        pix_valor: number;
        pagamento_conta_valor: number;
        voucher_valor: number;
        curso_valor: number;
        experiencia_valor: number;
        produto_valor: number;
      };
    });
  }, [empresasFiltradas, mesesAtivos]);

  // Recompensas consolidadas por tipo
  const recompensasConsolidadas = useMemo(() => {
    const tipos = ['pix', 'pagamento_conta', 'voucher', 'curso', 'experiencia', 'produto'] as const;
    return tipos.map((tipo) => {
      const qtd = empresasFiltradas.reduce((sum, e) => {
        const r = e.recompensas.find((r2) => r2.tipo === tipo);
        return sum + (r?.quantidade || 0);
      }, 0);
      const valor = empresasFiltradas.reduce((sum, e) => {
        const r = e.recompensas.find((r2) => r2.tipo === tipo);
        return sum + (r?.valor_total || 0);
      }, 0);
      return { tipo, quantidade: qtd, valor };
    });
  }, [empresasFiltradas]);

  const maxRecompensaValor = Math.max(...recompensasConsolidadas.map((r) => r.valor), 1);

  // Faturamento por plano
  const faturamentoPorPlano = useMemo(() => {
    const planos = ['basico', 'profissional', 'enterprise'] as const;
    return planos.map((plano) => {
      const valor = empresasAtivas.filter((e) => e.plano === plano).reduce((sum, e) => sum + e.faturamento_mensal, 0);
      return { plano, valor };
    });
  }, [empresasAtivas]);

  const totalFatPlano = faturamentoPorPlano.reduce((s, p) => s + p.valor, 0) || 1;

  const formatCurrency = (val: number) =>
    val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const formatNumber = (val: number) => val.toLocaleString('pt-BR');

  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    ativa: { bg: 'bg-emerald-50', text: 'text-emerald-600', label: 'Ativa' },
    pendente: { bg: 'bg-amber-50', text: 'text-amber-600', label: 'Pendente' },
    suspensa: { bg: 'bg-orange-50', text: 'text-orange-600', label: 'Suspensa' },
    inativa: { bg: 'bg-gray-50', text: 'text-gray-500', label: 'Inativa' },
  };

  const planoLabel = (p: string) =>
    p === 'basico' ? 'Básico' : p === 'profissional' ? 'Profissional' : 'Enterprise';

  const planoColor = (p: string) =>
    p === 'basico'
      ? 'bg-sky-50 text-sky-600'
      : p === 'profissional'
      ? 'bg-emerald-50 text-emerald-600'
      : 'bg-violet-50 text-violet-600';

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Dashboard Financeiro</h1>
            <p className="text-sm text-gray-500 mt-1">
              Visão consolidada do financeiro de todas as empresas clientes
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer"
            >
              <option value="3m">Últimos 3 meses</option>
              <option value="6m">Últimos 6 meses</option>
              <option value="ytd">Ano até hoje</option>
            </select>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer"
            >
              <option value="todos">Todos os status</option>
              <option value="ativa">Ativas</option>
              <option value="pendente">Pendentes</option>
              <option value="suspensa">Suspensas</option>
              <option value="inativa">Inativas</option>
            </select>
            <select
              value={filtroPlano}
              onChange={(e) => setFiltroPlano(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 cursor-pointer"
            >
              <option value="todos">Todos os planos</option>
              <option value="basico">Básico</option>
              <option value="profissional">Profissional</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Faturamento Mensal</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{formatCurrency(kpi.faturamentoTotal)}</p>
                <p className="text-xs text-emerald-600 mt-1 font-medium">{empresasAtivas.length} empresas ativas</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-emerald-600 text-lg"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Faturamento Acumulado</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{formatCurrency(kpi.faturamentoAcumulado)}</p>
                <p className="text-xs text-gray-400 mt-1">{periodoConfig.label}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-bar-chart-grouped-line text-violet-600 text-lg"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Participantes Ativos</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{formatNumber(kpi.participantesAtivos)}</p>
                <p className="text-xs text-blue-600 mt-1 font-medium">+{formatNumber(kpi.novosCadastros)} novos este mês</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-user-star-line text-blue-600 text-lg"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Recompensas Utilizadas</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{formatNumber(kpi.totalRecompensasQtd)}</p>
                <p className="text-xs text-amber-600 mt-1 font-medium">{formatCurrency(kpi.totalRecompensasValor)} em valor</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-gift-line text-amber-600 text-lg"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* YoY Section */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-line-chart-line text-gray-500 text-sm"></i>
            </div>
            <h2 className="text-sm font-bold text-gray-700">Comparativo Ano vs Ano Anterior</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <YoYBadget atual={kpi.faturamentoTotal} anterior={yoy.faturamentoTotal} label="Faturamento Mensal" isCurrency />
            <YoYBadget atual={kpi.faturamentoAcumulado} anterior={yoy.faturamentoAcumulado} label="Faturamento Acumulado" isCurrency />
            <YoYBadget atual={kpi.participantesAtivos} anterior={yoy.participantesAtivos} label="Participantes Ativos" />
            <YoYBadget atual={kpi.novosCadastros} anterior={yoy.novosCadastros} label="Novos Cadastros (Mês)" />
            <YoYBadget atual={kpi.totalRecompensasValor} anterior={yoy.totalRecompensasValor} label="Valor Recompensas" isCurrency />
            <YoYBadget atual={kpi.totalRecompensasQtd} anterior={yoy.totalRecompensasQtd} label="Qtd. Recompensas" />
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex border-b border-gray-100">
            {[
              { id: 'geral', label: 'Visão Geral', icon: 'ri-dashboard-line' },
              { id: 'faturamento', label: 'Faturamento', icon: 'ri-money-dollar-circle-line' },
              { id: 'cadastros', label: 'Cadastros', icon: 'ri-user-add-line' },
              { id: 'recompensas', label: 'Recompensas', icon: 'ri-gift-line' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setAbaAtiva(tab.id as typeof abaAtiva)}
                className={`flex items-center space-x-2 px-5 py-3 text-xs font-bold transition-all cursor-pointer whitespace-nowrap border-b-2 ${
                  abaAtiva === tab.id
                    ? 'border-emerald-500 text-emerald-600 bg-emerald-50/50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={tab.icon}></i>
                </div>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* ABA GERAL */}
            {abaAtiva === 'geral' && (
              <div className="space-y-8">
                {/* Faturamento + Cadastros lado a lado */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 mb-4">Faturamento Mensal Consolidado</h3>
                    <div className="flex items-end space-x-3 h-48 px-2">
                      {faturamentoHistorico.map((f) => (
                        <div key={f.mes} className="flex-1 flex flex-col items-center">
                          <div className="w-full flex justify-center">
                            <div
                              className="w-full max-w-[48px] bg-emerald-500 rounded-t-md transition-all relative group"
                              style={{ height: `${(f.valor / maxFaturamento) * 160}px` }}
                            >
                              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                {formatCurrency(f.valor)}
                              </div>
                            </div>
                          </div>
                          <span className="text-[10px] text-gray-500 mt-2 font-medium">{f.mes}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold text-gray-800 mb-4">Cadastros no Período</h3>
                    <div className="flex items-end space-x-3 h-48 px-2">
                      {cadastrosHistorico.map((c) => (
                        <div key={c.mes} className="flex-1 flex flex-col items-center">
                          <div className="w-full flex justify-center space-x-1">
                            <div
                              className="w-full max-w-[22px] bg-emerald-500 rounded-t-md transition-all relative group"
                              style={{ height: `${(c.novos / maxCadastros) * 160}px` }}
                            >
                              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                +{c.novos}
                              </div>
                            </div>
                            <div
                              className="w-full max-w-[22px] bg-red-400 rounded-t-md transition-all relative group"
                              style={{ height: `${(c.desligados / maxCadastros) * 160}px` }}
                            >
                              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                -{c.desligados}
                              </div>
                            </div>
                          </div>
                          <span className="text-[10px] text-gray-500 mt-2 font-medium">{c.mes}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-center gap-4 mt-3">
                      <div className="flex items-center space-x-1.5">
                        <div className="w-3 h-3 rounded-sm bg-emerald-500"></div>
                        <span className="text-[10px] text-gray-500">Novos</span>
                      </div>
                      <div className="flex items-center space-x-1.5">
                        <div className="w-3 h-3 rounded-sm bg-red-400"></div>
                        <span className="text-[10px] text-gray-500">Desligados</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Faturamento por Plano + Recompensas */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 mb-4">Faturamento por Plano</h3>
                    <div className="space-y-3">
                      {faturamentoPorPlano.map((fp) => {
                        const pct = totalFatPlano > 0 ? (fp.valor / totalFatPlano) * 100 : 0;
                        const barColor = fp.plano === 'basico' ? 'bg-sky-500' : fp.plano === 'profissional' ? 'bg-emerald-500' : 'bg-violet-500';
                        return (
                          <div key={fp.plano}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-gray-600">{planoLabel(fp.plano)}</span>
                              <span className="text-xs font-bold text-gray-800">
                                {formatCurrency(fp.valor)} ({Math.round(pct)}%)
                              </span>
                            </div>
                            <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between">
                      <span className="text-xs text-gray-500">Total mensal</span>
                      <span className="text-sm font-bold text-gray-800">{formatCurrency(totalFatPlano)}</span>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-bold text-gray-800 mb-4">Recompensas por Tipo</h3>
                    <div className="space-y-3">
                      {recompensasConsolidadas.map((r) => {
                        const color = getRecompensaTipoColor(r.tipo);
                        const pct = maxRecompensaValor > 0 ? (r.valor / maxRecompensaValor) * 100 : 0;
                        return (
                          <div key={r.tipo}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center space-x-2">
                                <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${color.bg} ${color.text}`}>
                                  {getRecompensaTipoLabel(r.tipo)}
                                </span>
                                <span className="text-xs text-gray-500">{formatNumber(r.quantidade)} usos</span>
                              </div>
                              <span className="text-xs font-bold text-gray-800">{formatCurrency(r.valor)}</span>
                            </div>
                            <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${color.bar}`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between">
                      <span className="text-xs text-gray-500">Total em recompensas</span>
                      <span className="text-sm font-bold text-gray-800">{formatCurrency(kpi.totalRecompensasValor)}</span>
                    </div>
                  </div>
                </div>

                {/* Gráfico de Recompensas */}
                {recompensasHistorico.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 mb-4">Evolução de Recompensas por Tipo</h3>
                    <RecompensasChart data={recompensasHistorico} />
                  </div>
                )}
              </div>
            )}

            {/* ABA FATURAMENTO */}
            {abaAtiva === 'faturamento' && (
              <div className="space-y-6">
                <h3 className="text-sm font-bold text-gray-800">Faturamento por Empresa</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Empresa</th>
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Plano</th>
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Faturamento Mensal</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Faturamento Acumulado</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Participantes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {empresasFiltradas
                        .sort((a, b) => b.faturamento_mensal - a.faturamento_mensal)
                        .map((e) => {
                          const st = statusConfig[e.status] || statusConfig.inativa;
                          return (
                            <tr
                              key={e.empresa_id}
                              onClick={() => navigate(`/backoffice/empresas/${e.empresa_id}`)}
                              className="hover:bg-gray-50 transition-colors cursor-pointer"
                            >
                              <td className="px-4 py-3">
                                <div>
                                  <p className="text-xs font-bold text-gray-800">{e.nome_fantasia}</p>
                                  <p className="text-[10px] text-gray-400">{e.cnpj}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${planoColor(e.plano)}`}>
                                  {planoLabel(e.plano)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${st.bg} ${st.text}`}>
                                  {st.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-xs font-bold text-gray-800">{formatCurrency(e.faturamento_mensal)}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-xs text-gray-600">{formatCurrency(e.faturamento_acumulado)}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-xs font-bold text-gray-800">{formatNumber(e.participantes_ativos)}</span>
                                <span className="text-[10px] text-gray-400"> / {formatNumber(e.participantes_total)}</span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                <div>
                  <h3 className="text-sm font-bold text-gray-800 mb-4">Evolução do Faturamento ({periodoConfig.label})</h3>
                  <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
                    {faturamentoHistorico.map((f) => (
                      <div key={f.mes} className="bg-gray-50 rounded-lg p-3 text-center">
                        <p className="text-[10px] text-gray-500 font-medium">{f.mes}</p>
                        <p className="text-sm font-bold text-gray-800 mt-1">{formatCurrency(f.valor)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ABA CADASTROS */}
            {abaAtiva === 'cadastros' && (
              <div className="space-y-6">
                <h3 className="text-sm font-bold text-gray-800">Cadastros por Empresa</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Empresa</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Ativos</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Total</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Novos (Mês)</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Taxa de Atividade</th>
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Evolução 6M</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {empresasFiltradas
                        .sort((a, b) => b.participantes_ativos - a.participantes_ativos)
                        .map((e) => {
                          const taxa = e.participantes_total > 0 ? (e.participantes_ativos / e.participantes_total) * 100 : 0;
                          const totalNovos = e.cadastros_historico.reduce((s, c) => s + c.novos, 0);
                          const totalDesligados = e.cadastros_historico.reduce((s, c) => s + c.desligados, 0);
                          return (
                            <tr
                              key={e.empresa_id}
                              onClick={() => navigate(`/backoffice/empresas/${e.empresa_id}`)}
                              className="hover:bg-gray-50 transition-colors cursor-pointer"
                            >
                              <td className="px-4 py-3">
                                <div>
                                  <p className="text-xs font-bold text-gray-800">{e.nome_fantasia}</p>
                                  <p className="text-[10px] text-gray-400">{e.cnpj}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-xs font-bold text-gray-800">{formatNumber(e.participantes_ativos)}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-xs text-gray-600">{formatNumber(e.participantes_total)}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="text-xs font-bold text-emerald-600">+{formatNumber(e.novos_cadastros_mes)}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end space-x-2">
                                  <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full rounded-full ${taxa >= 80 ? 'bg-emerald-500' : taxa >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                                      style={{ width: `${taxa}%` }}
                                    />
                                  </div>
                                  <span className="text-xs font-bold text-gray-800">{Math.round(taxa)}%</span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center space-x-1">
                                  <span className="text-[10px] text-emerald-600 font-medium">+{totalNovos}</span>
                                  <span className="text-[10px] text-gray-400">/</span>
                                  <span className="text-[10px] text-red-400 font-medium">-{totalDesligados}</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ABA RECOMPENSAS */}
            {abaAtiva === 'recompensas' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  {recompensasConsolidadas.map((r) => {
                    const color = getRecompensaTipoColor(r.tipo);
                    return (
                      <div key={r.tipo} className="bg-white rounded-xl p-4 border border-gray-100">
                        <div className="flex items-center space-x-2 mb-3">
                          <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${color.bg} ${color.text}`}>
                            {getRecompensaTipoLabel(r.tipo)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-[10px] text-gray-400 font-medium">Quantidade</p>
                            <p className="text-lg font-bold text-gray-800">{formatNumber(r.quantidade)}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-400 font-medium">Valor Total</p>
                            <p className="text-lg font-bold text-gray-800">{formatCurrency(r.valor)}</p>
                          </div>
                        </div>
                        <div className="mt-3">
                          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${color.bar}`}
                              style={{ width: `${maxRecompensaValor > 0 ? (r.valor / maxRecompensaValor) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <h3 className="text-sm font-bold text-gray-800 mt-4">Recompensas por Empresa</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Empresa</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">PIX</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Contas</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Vouchers</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Cursos</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Experiências</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Produtos</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {empresasFiltradas
                        .sort((a, b) => b.total_recompensas_valor - a.total_recompensas_valor)
                        .map((e) => (
                          <tr
                            key={e.empresa_id}
                            onClick={() => navigate(`/backoffice/empresas/${e.empresa_id}`)}
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <td className="px-4 py-3">
                              <p className="text-xs font-bold text-gray-800">{e.nome_fantasia}</p>
                            </td>
                            {['pix', 'pagamento_conta', 'voucher', 'curso', 'experiencia', 'produto'].map((tipo) => {
                              const r = e.recompensas.find((r2) => r2.tipo === tipo);
                              return (
                                <td key={tipo} className="px-4 py-3 text-right">
                                  <p className="text-xs font-bold text-gray-800">
                                    {r ? formatNumber(r.quantidade) : '0'}
                                  </p>
                                  <p className="text-[10px] text-gray-400">
                                    {r ? formatCurrency(r.valor_total) : 'R$ 0,00'}
                                  </p>
                                </td>
                              );
                            })}
                            <td className="px-4 py-3 text-right">
                              <p className="text-xs font-bold text-gray-800">{formatNumber(e.total_recompensas_quantidade)}</p>
                              <p className="text-[10px] text-emerald-600 font-medium">{formatCurrency(e.total_recompensas_valor)}</p>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabela consolidada geral */}
        {abaAtiva === 'geral' && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800">Resumo por Empresa</h3>
              <span className="text-[10px] text-gray-400">{empresasFiltradas.length} empresas encontradas</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Empresa</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Plano</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Faturamento</th>
                    <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Participantes</th>
                    <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Novos (Mês)</th>
                    <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Recompensas</th>
                    <th className="text-right px-4 py-3 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Valor Rec.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {empresasFiltradas.map((e) => {
                    const st = statusConfig[e.status] || statusConfig.inativa;
                    return (
                      <tr
                        key={e.empresa_id}
                        onClick={() => navigate(`/backoffice/empresas/${e.empresa_id}`)}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-xs font-bold text-gray-800">{e.nome_fantasia}</p>
                            <p className="text-[10px] text-gray-400">{e.cnpj}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${planoColor(e.plano)}`}>
                            {planoLabel(e.plano)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase ${st.bg} ${st.text}`}>
                            {st.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-xs font-bold text-gray-800">{formatCurrency(e.faturamento_mensal)}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-xs font-bold text-gray-800">{formatNumber(e.participantes_ativos)}</span>
                          <span className="text-[10px] text-gray-400"> / {formatNumber(e.participantes_total)}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-xs font-bold text-emerald-600">+{e.novos_cadastros_mes}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-xs font-bold text-gray-800">{formatNumber(e.total_recompensas_quantidade)}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-xs font-bold text-gray-800">{formatCurrency(e.total_recompensas_valor)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {empresasFiltradas.length === 0 && (
              <div className="p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl text-gray-300"></i>
                </div>
                <p className="text-sm text-gray-400">Nenhuma empresa encontrada com os filtros selecionados</p>
              </div>
            )}
          </div>
        )}
      </div>
    </BackofficeLayout>
  );
}